import React, { useState, useEffect } from "react";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "";

function SettingsModal({ onClose }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/settings`, { withCredentials: true });
      setSettings(data);
    } catch (err) {
      setMessage("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage("");
    try {
      await axios.put(`${API}/api/settings`, settings, { withCredentials: true });
      setMessage("Settings saved successfully");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      setMessage("Failed to save: " + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const updateField = (path, value) => {
    const keys = path.split(".");
    const updated = JSON.parse(JSON.stringify(settings));
    let obj = updated;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!obj[keys[i]]) obj[keys[i]] = {};
      obj = obj[keys[i]];
    }
    obj[keys[keys.length - 1]] = value;
    setSettings(updated);
  };

  if (loading) return <div className="settings-modal"><div className="settings-content"><p>Loading...</p></div></div>;

  return (
    <div className="settings-modal" onClick={onClose}>
      <div className="settings-content" onClick={(e) => e.stopPropagation()}>
        <div className="settings-header">
          <h2>⚙️ Tenant Settings</h2>
          <button className="settings-close" onClick={onClose}>✕</button>
        </div>

        {message && <div className={`settings-msg ${message.includes("success") ? "settings-msg-ok" : "settings-msg-err"}`}>{message}</div>}

        <div className="settings-body">
          {/* Features */}
          <div className="settings-section">
            <h3>Features</h3>
            <div className="settings-grid">
              <label>
                <input type="checkbox" checked={settings?.features?.duplicate_detection_enabled || false}
                  onChange={(e) => updateField("features.duplicate_detection_enabled", e.target.checked)} />
                Duplicate Detection
              </label>
              <label>
                <input type="checkbox" checked={settings?.features?.forward_detection_enabled || false}
                  onChange={(e) => updateField("features.forward_detection_enabled", e.target.checked)} />
                Forward Detection
              </label>
            </div>
          </div>

          {/* Validation */}
          <div className="settings-section">
            <h3>Falcon Push</h3>
            <div className="settings-grid">
              <label>
                <input type="checkbox" checked={settings?.falcon_schema_value_validation === true || settings?.falcon_schema_value_validation === "true"}
                  onChange={(e) => updateField("falcon_schema_value_validation", e.target.checked)} />
                Schema Value Validation
              </label>
            </div>
          </div>

          {/* Azure Pricing */}
          <div className="settings-section">
            <h3>Azure DI Pricing</h3>
            <div className="settings-grid">
              <div className="settings-field">
                <label>Cost per Page ($)</label>
                <input type="number" step="0.001" value={settings?.azure?.di_cost_per_page || ""}
                  onChange={(e) => updateField("azure.di_cost_per_page", parseFloat(e.target.value) || 0)} />
              </div>
            </div>
          </div>

          {/* OpenAI Pricing */}
          <div className="settings-section">
            <h3>OpenAI Pricing (per 1M tokens)</h3>
            <div className="settings-grid">
              <div className="settings-field">
                <label>Input Cost ($/1M tokens)</label>
                <input type="number" step="0.01" value={settings?.openai?.input_cost_per_1m_tokens || ""}
                  onChange={(e) => updateField("openai.input_cost_per_1m_tokens", parseFloat(e.target.value) || 0)} />
              </div>
              <div className="settings-field">
                <label>Output Cost ($/1M tokens)</label>
                <input type="number" step="0.01" value={settings?.openai?.output_cost_per_1m_tokens || ""}
                  onChange={(e) => updateField("openai.output_cost_per_1m_tokens", parseFloat(e.target.value) || 0)} />
              </div>
              <div className="settings-field">
                <label>Model</label>
                <input type="text" value={settings?.openai?.model || ""}
                  onChange={(e) => updateField("openai.model", e.target.value)} />
              </div>
            </div>
          </div>

          {/* Processing Settings */}
          <div className="settings-section">
            <h3>Processing</h3>
            <div className="settings-grid">
              <div className="settings-field">
                <label>Max Concurrent Messages</label>
                <input type="number" value={settings?.processing_settings?.max_concurrent_messages || ""}
                  onChange={(e) => updateField("processing_settings.max_concurrent_messages", parseInt(e.target.value) || 3)} />
              </div>
              <div className="settings-field">
                <label>Duplicate Similarity Threshold</label>
                <input type="number" step="0.01" min="0" max="1" value={settings?.processing_settings?.duplicate_similarity_threshold || ""}
                  onChange={(e) => updateField("processing_settings.duplicate_similarity_threshold", parseFloat(e.target.value) || 0.85)} />
              </div>
              <div className="settings-field">
                <label>Duplicate Time Window (min)</label>
                <input type="number" value={settings?.processing_settings?.duplicate_time_window_minutes || ""}
                  onChange={(e) => updateField("processing_settings.duplicate_time_window_minutes", parseInt(e.target.value) || 30)} />
              </div>
            </div>
          </div>

          {/* Limits */}
          <div className="settings-section">
            <h3>Limits</h3>
            <div className="settings-grid">
              <div className="settings-field">
                <label>Max Attachment Size (MB)</label>
                <input type="number" value={settings?.limits?.max_attachment_size_mb || ""}
                  onChange={(e) => updateField("limits.max_attachment_size_mb", parseInt(e.target.value) || 50)} />
              </div>
              <div className="settings-field">
                <label>Max Emails per Sync</label>
                <input type="number" value={settings?.limits?.max_emails_per_sync || ""}
                  onChange={(e) => updateField("limits.max_emails_per_sync", parseInt(e.target.value) || 1000)} />
              </div>
            </div>
          </div>

          {/* Notifications */}
          <div className="settings-section">
            <h3>Notifications</h3>
            <div className="settings-grid">
              <label>
                <input type="checkbox" checked={settings?.notification_settings?.email_notifications_enabled || false}
                  onChange={(e) => updateField("notification_settings.email_notifications_enabled", e.target.checked)} />
                Email Notifications
              </label>
              <label>
                <input type="checkbox" checked={settings?.notification_settings?.webhook_notifications_enabled || false}
                  onChange={(e) => updateField("notification_settings.webhook_notifications_enabled", e.target.checked)} />
                Webhook Notifications
              </label>
            </div>
          </div>
        </div>

        <div className="settings-footer">
          <button className="settings-save" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save Settings"}
          </button>
          <button className="settings-cancel" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default SettingsModal;
